import { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";
import { cn } from "../utils/cn";

type TagPillInputProps = {
  value: string[];
  onChange: (tags: string[]) => void;
  suggestedValues: string[];
  placeholder?: string;
  disabled?: boolean;
  "aria-label"?: string;
};

/**
 * Inline tag input: pills for current tags, click to enter "add" mode.
 * Filter list as you type; click or Enter to add (existing or new).
 */
export function TagPillInput({
  value,
  onChange,
  suggestedValues,
  placeholder = "Ajouter un tag…",
  disabled = false,
  "aria-label": ariaLabel = "Tags",
}: TagPillInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredSuggestions = suggestedValues.filter(
    (s) =>
      s &&
      !value.includes(s) &&
      (!inputValue.trim() || s.toLowerCase().includes(inputValue.trim().toLowerCase()))
  );
  const exactMatch = inputValue.trim() && filteredSuggestions.some((s) => s === inputValue.trim());
  const canAddNew = inputValue.trim() && !value.includes(inputValue.trim());

  useEffect(() => {
    if (!isOpen) return;
    setHighlightIndex(0);
  }, [inputValue, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [isOpen]);

  const addTag = (tag: string) => {
    const t = tag.trim();
    if (!t || value.includes(t)) return;
    onChange([...value, t]);
    setInputValue("");
    setHighlightIndex(0);
    if (filteredSuggestions.length <= 1 && !canAddNew) setIsOpen(false);
  };

  const removeTag = (tag: string) => {
    onChange(value.filter((t) => t !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const toAdd =
        filteredSuggestions[highlightIndex] ?? (canAddNew ? inputValue.trim() : null);
      if (toAdd) {
        addTag(toAdd);
      }
      return;
    }
    if (e.key === "Escape") {
      setIsOpen(false);
      setInputValue("");
      inputRef.current?.blur();
      return;
    }
    if (e.key === "Backspace" && !inputValue && value.length > 0) {
      removeTag(value[value.length - 1]!);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, filteredSuggestions.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    }
  };

  return (
    <div ref={containerRef} className="space-y-1">
      <div
        className={cn(
          "min-h-9 rounded-md border border-border bg-surface px-2 py-1.5 flex flex-wrap items-center gap-1.5",
          isOpen && "ring-2 ring-primary/30 border-primary/50",
          disabled && "opacity-60 pointer-events-none"
        )}
        onClick={() => !disabled && (setIsOpen(true), setTimeout(() => inputRef.current?.focus(), 0))}
      >
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-muted px-2.5 py-0.5 text-xs text-text"
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(tag);
                }}
                className="rounded p-0.5 hover:bg-danger/10 text-muted hover:text-danger"
                aria-label={`Retirer ${tag}`}
              >
                <X size={12} />
              </button>
            )}
          </span>
        ))}
        {isOpen ? (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            aria-label={ariaLabel}
            className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-sm text-text placeholder:text-muted py-0.5"
          />
        ) : (
          <span className="text-sm text-muted cursor-text py-0.5">
            {placeholder}
          </span>
        )}
      </div>
      {isOpen && (filteredSuggestions.length > 0 || canAddNew) && (
        <div
          className="rounded-md border border-border bg-surface shadow-lg py-1 max-h-48 overflow-y-auto z-10"
          role="listbox"
        >
          {filteredSuggestions.map((s, i) => (
            <button
              key={s}
              type="button"
              role="option"
              aria-selected={i === highlightIndex}
              className={cn(
                "w-full text-left px-3 py-2 text-sm text-text hover:bg-surface-muted",
                i === highlightIndex && "bg-surface-muted"
              )}
              onMouseEnter={() => setHighlightIndex(i)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                addTag(s);
              }}
            >
              {s}
            </button>
          ))}
          {canAddNew && !exactMatch && (
            <button
              type="button"
              role="option"
              aria-selected={filteredSuggestions.length === highlightIndex}
              className={cn(
                "w-full text-left px-3 py-2 text-sm text-primary hover:bg-surface-muted",
                filteredSuggestions.length === highlightIndex && "bg-surface-muted"
              )}
              onMouseEnter={() => setHighlightIndex(filteredSuggestions.length)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                addTag(inputValue.trim());
              }}
            >
              + Créer « {inputValue.trim()} »
            </button>
          )}
        </div>
      )}
    </div>
  );
}
